import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../api";

import HeadquartersPage from "../components/headquarters/HeadquartersPage";
import HeadquartersSection from "../components/headquarters/HeadquartersSection";
import HeadquartersGrid from "../components/headquarters/HeadquartersGrid";
import HeadquartersMetricCard from "../components/headquarters/HeadquartersMetricCard";

export default function PlatformDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIntelligence();
  }, []);

  async function loadIntelligence() {
    setLoading(true);
    setErr("");

    try {
      const result = await apiRequest("/platform/intelligence");
      setData(result);
    } catch (error) {
      setErr(error.message || "Failed to load platform intelligence.");
    } finally {
      setLoading(false);
    }
  }

  const summary = data?.summary || {};
  const totals = data?.operational_totals || {};

  const actionBtn = {
    padding: "12px 18px",
    borderRadius: 8,
    background: "#e0b84f",
    color: "#0b1b2b",
    fontWeight: 800,
    cursor: "pointer",
    border: "none",
    minWidth: 220,
    textAlign: "center",
  };

  return (
    <HeadquartersPage
      title="Platform Headquarters"
      subtitle="Live command center for JobTelesis tenant health, billing state, and workflow activity."
    >
      {err && (
        <HeadquartersSection
          style={{
            border: "1px solid rgba(248,113,113,0.35)",
            background: "rgba(127,29,29,0.25)",
            color: "#fecaca",
          }}
        >
          {err}
        </HeadquartersSection>
      )}

      {loading && (
        <HeadquartersSection>
          Loading platform intelligence...
        </HeadquartersSection>
      )}

      {!loading && data && (
        <>
          <HeadquartersSection
            title="Command Center"
            subtitle="Primary operational systems for platform management, tenant intelligence, billing authority, and future SaaS infrastructure."
          >
            <HeadquartersGrid min={220} gap={14}>
              <button style={actionBtn} onClick={() => navigate("/platform/tenant-intelligence")}>
                Tenant Intelligence
              </button>

              <button style={actionBtn} onClick={() => navigate("/platform/tenants")}>
                Tenant Management
              </button>

              <button style={actionBtn} onClick={() => navigate("/platform/billing")}>
                Billing Control
              </button>

              <button style={actionBtn} onClick={() => navigate("/platform/stripe-billing")}>
                Stripe Billing Authority
              </button>

              <button style={actionBtn} onClick={() => navigate("/platform/stripe-connect")}>
                Stripe Connect Payments
              </button>

              <button style={actionBtn} onClick={() => alert("Communications page coming next")}>
                Communications
              </button>

              <button style={actionBtn} onClick={() => navigate("/platform/beta-controls")}>
                Beta Controls
              </button>

              <button style={actionBtn} onClick={() => alert("AI Control page coming next")}>
                AI Control
              </button>
            </HeadquartersGrid>
          </HeadquartersSection>

          <HeadquartersSection
            title="Platform Summary"
            subtitle="Tenant count, subscription posture, and platform access overview."
          >
            <HeadquartersGrid min={150}>
              <HeadquartersMetricCard label="Total Tenants" value={summary.total_tenants ?? 0} />
              <HeadquartersMetricCard label="Active Tenants" value={summary.active_tenants ?? 0} />
              <HeadquartersMetricCard label="Trial Tenants" value={summary.trial_tenants ?? 0} />
              <HeadquartersMetricCard label="Free / Internal" value={summary.free_tenants ?? 0} />
              <HeadquartersMetricCard label="Paid Tenants" value={summary.paid_tenants ?? 0} />
              <HeadquartersMetricCard label="Suspended" value={summary.suspended_tenants ?? 0} />
            </HeadquartersGrid>
          </HeadquartersSection>

          <HeadquartersSection
            title="Operational Activity"
            subtitle="Workflow usage across all tenants: clients, jobs, estimates, invoices, and payments."
          >
            <HeadquartersGrid min={150}>
              <HeadquartersMetricCard label="Clients" value={totals.clients ?? 0} />
              <HeadquartersMetricCard label="Jobs" value={totals.jobs ?? 0} />
              <HeadquartersMetricCard label="Estimates" value={totals.estimates ?? 0} />
              <HeadquartersMetricCard label="Invoices" value={totals.invoices ?? 0} />
              <HeadquartersMetricCard label="Payments / Receipts" value={totals.payments ?? 0} />
            </HeadquartersGrid>
          </HeadquartersSection>

          <HeadquartersSection
            title="Platform Controls"
            subtitle="Engineering, deployment, release management, communications, feature management, and future platform operations."
          >
            <HeadquartersGrid min={220} gap={14}>
              <button style={actionBtn} onClick={() => navigate("/platform/release-center")}>
                Release Center
              </button>

              <button style={actionBtn} onClick={() => alert("Communications page coming next")}>
                Communications
              </button>

              <button style={actionBtn} onClick={() => alert("Feature Flags page coming next")}>
                Feature Flags
              </button>

              <button style={actionBtn} onClick={() => alert("Platform cost tracking coming next")}>
                Cost Tracking
              </button>
            </HeadquartersGrid>
          </HeadquartersSection>
        </>
      )}
    </HeadquartersPage>
  );
}
